import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { canUseOrders } from "../../../helpers/orderEnabled";
import { readAuthUser } from "../../../helpers/studioModules";
import {
  addMemoParcel,
  checkParcelNow,
  completeParcelSlipUpload,
  editParcel,
  getParcel,
  listDispatchCouriers,
  listMemoParcels,
  listOverdueParcels,
  removeParcel,
  setParcelStage,
  startParcelSlipUpload,
  syncParcelZoho,
  updateDispatchCourier,
  type DispatchCourier,
  type ParcelCourierInput,
  type ParcelDetail,
  type ParcelView,
} from "../../../services/studioSalesApi";
import SalesSheet from "./SalesSheet";
import {
  OVERDUE_DAYS,
  PARCEL_ROLES,
  SLIP_MAX_BYTES,
  STAGES,
  dayLabel,
  isParcelsOff,
  isValidAwb,
  overdueText,
  parcelMode,
  shareText,
  slipFileType,
  stageSourceText,
  deliveredButtonDue,
  checkLine,
  stageText,
  templateFromPastedUrl,
  templateProblem,
  todayIso,
  whatsAppShareHref,
  zohoLine,
} from "./parcelModel";

/** Studio admin or manager of a studio with the orders module. The server's flag decides the rest (404 = off). */
export const canUseParcels = (role?: string): boolean => canUseOrders(role) && PARCEL_ROLES.includes(role || "");

const offline = "Check your connection and try again";
const msg = (res: { message?: string } | null | undefined, fallback: string) => {
  const text = res?.message || "";
  // Internal codes and the generic server failure say nothing useful to a person.
  return !text || /^[A-Z_]+$/.test(text) || /exception/i.test(text) ? fallback : text;
};

type PhotoJob = { file: File; state: "uploading" | "failed"; message?: string };
export type ParcelInput = ParcelCourierInput & { awbNumber: string; bookedOn?: string };
type Done = { ok: true } | { ok: false; message: string };

export type MemoParcels = {
  /** "edit" on a billed sale or an approval memo that went out, "read" on a cancelled one, null: no card. */
  mode: "edit" | "read" | null;
  status: "off" | "loading" | "error" | "ready";
  parcels: ParcelView[];
  /** Other memos that already carried an AWB when it was added here, by parcel id. */
  duplicates: Record<string, string[]>;
  photos: Record<string, PhotoJob>;
  announce: string;
  busy: boolean;
  reload: () => void;
  add: (input: ParcelInput, slip?: File | null) => Promise<Done>;
  edit: (parcel: ParcelView, input: Partial<ParcelInput>) => Promise<Done>;
  markDelivered: (parcel: ParcelView, date: string, note: string) => Promise<Done>;
  remove: (parcel: ParcelView, reason: string) => Promise<Done>;
  uploadSlip: (parcelId: string, file: File) => Promise<void>;
  /** Writes the memo's parcels into its Zoho notes again. */
  retryZoho: (parcel: ParcelView) => Promise<Done>;
  /** "Check now": asks the courier once (at most hourly). The text says what happened, success or not. */
  checkNow: (parcel: ParcelView) => Promise<{ ok: boolean; message: string }>;
};

/** What a finished "Check now" says on the row. */
export const checkNowText = (parcel: ParcelView): string => {
  if (parcel.checkState === "FAILED") return "Couldn't reach the courier just now. Try again later or open the courier page.";
  const label = parcel.courierStatusText || (STAGES[parcel.stage] || STAGES.BOOKED).label;
  return `Checked just now: ${label}`;
};

/** How often, and how many times, a list with a parcel still "Sending to Zoho…" is read again. */
export const ZOHO_RECHECK_MS = 4000;
const ZOHO_RECHECKS = 4;

/** The memo's parcels. Nothing is fetched unless the memo and the caller may have parcels. */
export const useMemoParcels = ({ memo, role }: { memo: any; role?: string }): MemoParcels => {
  const mode = canUseParcels(role) ? parcelMode(memo) : null;
  const memoId: string = memo?.id || "";
  const [status, setStatus] = useState<MemoParcels["status"]>(mode && memoId ? "loading" : "off");
  const [parcels, setParcels] = useState<ParcelView[]>([]);
  const [duplicates, setDuplicates] = useState<Record<string, string[]>>({});
  const [photos, setPhotos] = useState<Record<string, PhotoJob>>({});
  const [announce, setAnnounce] = useState("");
  const [busy, setBusy] = useState(false);
  const [round, setRound] = useState(0);
  const busyRef = useRef(false);
  // The memo screen stays mounted when /sales/memos/:id changes: drop the last memo's parcels before painting, and
  // let no answer that was meant for it land on this one.
  const [shownFor, setShownFor] = useState(memoId);
  const memoRef = useRef(memoId);
  memoRef.current = memoId;
  if (shownFor !== memoId) {
    setShownFor(memoId);
    setParcels([]);
    setDuplicates({});
    setPhotos({});
    setAnnounce("");
    setStatus(mode && memoId ? "loading" : "off");
  }

  useEffect(() => {
    if (!mode || !memoId) {
      setStatus("off");
      return;
    }
    let live = true;
    setStatus((cur) => (cur === "ready" ? cur : "loading"));
    Promise.resolve(listMemoParcels(memoId))
      .then((res) => {
        if (!live) return;
        if (isParcelsOff(res)) return setStatus("off");
        if (!res?.success) return setStatus("error");
        setParcels(Array.isArray(res.data) ? res.data : []);
        setStatus("ready");
      })
      .catch(() => live && setStatus("error"));
    return () => { live = false; };
  }, [mode, memoId, round]);

  const reload = useCallback(() => setRound((n) => n + 1), []);

  // The Zoho notes are written in the background after a save: read the list again a few times while one is sending.
  const rechecks = useRef(0);
  const sending = status === "ready" && parcels.some((p) => p.zohoSyncState === "PENDING");
  useEffect(() => {
    if (!sending) {
      rechecks.current = 0;
      return;
    }
    if (rechecks.current >= ZOHO_RECHECKS) return;
    const timer = window.setTimeout(() => {
      rechecks.current += 1;
      reload();
    }, ZOHO_RECHECK_MS);
    return () => window.clearTimeout(timer);
  }, [sending, parcels, reload]);
  const replace = (next: ParcelView) => setParcels((cur) => cur.map((p) => (p.id === next.id ? next : p)));

  /** One write at a time; a stale or conflicting write reloads the list so the screen shows what the server holds. */
  const write = useCallback(async <T,>(fallback: string, request: () => Promise<{ success: boolean; status?: number; message?: string; data: T | null }>,
    done: (data: T) => void): Promise<Done> => {
    if (busyRef.current) return { ok: false, message: "Wait a moment" };
    busyRef.current = true;
    setBusy(true);
    const forMemo = memoRef.current;
    try {
      const res = await request();
      if (memoRef.current !== forMemo) return { ok: true };
      if (!res?.success || res.data == null) {
        if (res?.status === 409 || res?.status === 404) reload();
        return { ok: false, message: msg(res, fallback) };
      }
      done(res.data);
      return { ok: true };
    } catch {
      return { ok: false, message: offline };
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [reload]);

  const uploadSlip = useCallback(async (parcelId: string, file: File) => {
    const forMemo = memoRef.current;
    const fail = (message: string) => {
      if (memoRef.current !== forMemo) return;
      setPhotos((cur) => ({ ...cur, [parcelId]: { file, state: "failed", message } }));
      setAnnounce(`Slip photo not saved. ${message}`);
    };
    const type = slipFileType(file);
    if (!type) return fail("Use a JPG, PNG or HEIC photo.");
    if (file.size > SLIP_MAX_BYTES) return fail("Photos can be up to 10 MB.");
    setPhotos((cur) => ({ ...cur, [parcelId]: { file, state: "uploading" } }));
    try {
      const start = await startParcelSlipUpload(parcelId, type);
      if (!start?.success || !start.data) return fail(msg(start, "Try again."));
      const put = await fetch(start.data.uploadUrl, { method: "PUT", headers: { "Content-Type": start.data.contentType }, body: file });
      if (!put.ok) return fail("The upload did not finish. Try again.");
      const done = await completeParcelSlipUpload(parcelId);
      if (!done?.success || !done.data) return fail(msg(done, "Try again."));
      if (memoRef.current !== forMemo) return;
      replace(done.data.parcel);
      setPhotos((cur) => {
        const next = { ...cur };
        delete next[parcelId];
        return next;
      });
      setAnnounce("Slip photo saved");
    } catch {
      fail(offline);
    }
  }, []);

  const add = useCallback(async (input: ParcelInput, slip?: File | null) => {
    let added: ParcelView | null = null;
    const result = await write("Parcel not added", () => addMemoParcel(memoId, input), (data) => {
      added = data.parcel;
      setParcels((cur) => [...cur, data.parcel]);
      if (data.duplicateOf?.length) setDuplicates((cur) => ({ ...cur, [data.parcel.id]: data.duplicateOf }));
      setAnnounce(`Parcel added: ${data.parcel.courierName}, AWB ${data.parcel.awbNumber}`);
    });
    // Saving never waits for the photo: it uploads after, with its own progress and retry.
    const saved = added as ParcelView | null;
    if (saved && slip) void uploadSlip(saved.id, slip);
    return result;
  }, [memoId, uploadSlip, write]);

  const edit = useCallback((parcel: ParcelView, input: Partial<ParcelInput>) =>
    write("Parcel not changed", () => editParcel(parcel.id, { ...input, version: parcel.version }), (data) => {
      replace(data);
      setAnnounce("Parcel changed");
    }), [write]);

  const markDelivered = useCallback((parcel: ParcelView, date: string, note: string) =>
    write("Not marked delivered", () => setParcelStage(parcel.id, { stage: "DELIVERED", date, reason: note.trim() || undefined }), (data) => {
      replace(data);
      setAnnounce(`Marked delivered on ${dayLabel(data.deliveredOn) || dayLabel(date)}`);
    }), [write]);

  const remove = useCallback((parcel: ParcelView, reason: string) =>
    write("Parcel not removed", () => removeParcel(parcel.id, reason.trim()), () => {
      setParcels((cur) => cur.filter((p) => p.id !== parcel.id));
      setAnnounce(`Parcel ${parcel.awbNumber} removed`);
    }), [write]);

  const retryZoho = useCallback((parcel: ParcelView) =>
    write("Not sent to Zoho", () => syncParcelZoho(parcel.id), (data) => {
      // The state is the memo's: every row shows the same line.
      setParcels((cur) => cur.map((p) => (p.id === data.id ? data
        : { ...p, zohoSyncState: data.zohoSyncState, zohoSyncedAt: data.zohoSyncedAt, zohoSyncError: data.zohoSyncError })));
      setAnnounce(data.zohoSyncState === "SYNCED" ? "AWB is in the Zoho notes" : `Not in Zoho. ${data.zohoSyncError || ""}`.trim());
    }), [write]);

  const checkNow = useCallback(async (parcel: ParcelView) => {
    let text = "";
    const done = await write("Not checked", () => checkParcelNow(parcel.id), (data) => {
      replace(data);
      text = checkNowText(data);
      setAnnounce(text);
    });
    return done.ok ? { ok: true, message: text } : { ok: false, message: done.message };
  }, [write]);

  return { mode, status, parcels, duplicates, photos, announce, busy, reload, add, edit, markDelivered, remove, uploadSlip, retryZoho, checkNow };
};

/** Desktop (>= 1024 px) puts each parcel on one row with its actions inline. */
const useWide = () => {
  const query = "(min-width: 1024px)";
  const get = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches;
  const [wide, setWide] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const list = window.matchMedia(query);
    const on = () => setWide(list.matches);
    list.addEventListener?.("change", on);
    return () => list.removeEventListener?.("change", on);
  }, []);
  return wide;
};

type Sheet =
  | { kind: "add" }
  | { kind: "edit" | "delivered" | "remove" | "menu" | "slip"; parcel: ParcelView }
  | { kind: "copy"; text: string };

const canShareNatively = () => typeof navigator !== "undefined" && typeof navigator.share === "function";

/**
 * Parcels on the memo screen: courier, AWB, stage and the 15-day flag, with the customer's WhatsApp message one
 * tap away. Renders nothing while the feature is off, the caller may not keep parcels, or the memo has none to show.
 */
export const ParcelsCard = ({ memo, parcels, partyPhone }: { memo: any; parcels: MemoParcels; partyPhone?: string }) => {
  const headingId = useId();
  const wide = useWide();
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [copied, setCopied] = useState("");
  /** The last "Check now" answer per parcel, shown on its row until the next one. */
  const [checked, setChecked] = useState<Record<string, { ok: boolean; message: string }>>({});
  const close = useCallback(() => setSheet(null), []);
  const { mode, status, parcels: list } = parcels;
  const editing = mode === "edit";

  const order: string = memo?.invoiceNumber || memo?.zohoChallanNumber || memo?.memoCode || "";
  const studio: string = readAuthUser()?.users?.organizationResponse?.name || "";

  if (!mode || status === "off" || status === "loading") return null;
  // A cancelled memo only shows the parcels it had.
  if (mode === "read" && status === "ready" && list.length === 0) return null;

  const share = async (items: ParcelView[]) => {
    const text = shareText(items, order, studio);
    if (canShareNatively()) {
      try {
        await navigator.share({ text });
        return;
      } catch (e: any) {
        if (e?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied("Message copied. Paste it in WhatsApp.");
    } catch {
      setSheet({ kind: "copy", text });
    }
  };
  const phoneHref = (items: ParcelView[]) => whatsAppShareHref(partyPhone, shareText(items, order, studio));
  const shareLabel = partyPhone && phoneHref(list) ? "WhatsApp" : canShareNatively() ? "Share" : "Copy text";
  const shareControl = (items: ParcelView[], label: string, extra = "") => {
    const href = phoneHref(items);
    return href ? (
      <a className={`pc-act${extra}`} href={href} target="_blank" rel="noopener noreferrer">{label}</a>
    ) : (
      <button type="button" className={`pc-act${extra}`} onClick={() => void share(items)}>{label}</button>
    );
  };

  const rowActions = (p: ParcelView) => {
    const actions: Array<{ key: string; label: string; run: () => void; danger?: boolean }> = [];
    if (!editing) {
      if (p.hasSlipPhoto) actions.push({ key: "slip", label: "View slip", run: () => setSheet({ kind: "slip", parcel: p }) });
      return actions;
    }
    if (p.autoCheck && p.stage !== "DELIVERED" && p.stage !== "RETURNED") {
      actions.push({ key: "check", label: "Check now", run: () => {
        setSheet(null);
        void parcels.checkNow(p).then((r) => setChecked((cur) => ({ ...cur, [p.id]: r })));
      } });
    }
    // From day 3 it is a button on the parcel itself; before that it waits in the menu.
    if (p.stage !== "DELIVERED" && !deliveredButtonDue(p)) {
      actions.push({ key: "delivered", label: "Mark delivered", run: () => setSheet({ kind: "delivered", parcel: p }) });
    }
    if (p.stage === "BOOKED") actions.push({ key: "edit", label: "Edit", run: () => setSheet({ kind: "edit", parcel: p }) });
    if (p.hasSlipPhoto) actions.push({ key: "slip", label: "View slip", run: () => setSheet({ kind: "slip", parcel: p }) });
    actions.push({ key: "remove", label: "Remove", danger: true, run: () => setSheet({ kind: "remove", parcel: p }) });
    return actions;
  };

  const row = (p: ParcelView) => {
    const info = STAGES[p.stage] || STAGES.BOOKED;
    const check = checkLine(p);
    // A person's mark names them; otherwise an automatic check says when it last looked.
    const source = stageSourceText(p) || (check && !check.failed ? check.text : "");
    const late = overdueText(p);
    const dupes = parcels.duplicates[p.id] || [];
    const photo = parcels.photos[p.id];
    const actions = rowActions(p);
    const zoho = zohoLine(p);
    return (
      <li key={p.id} className={`pc-row${wide ? " is-wide" : ""}`}>
        <div className="pc-row__main">
          <span className={`sp-st is-${info.tone} pc-stage`}><span aria-hidden="true">{info.glyph} </span>{stageText(p)}</span>
          <span className="t16 pc-courier">{p.courierName}</span>
          <span className="mono pc-awb" title={p.awbNumber}>{p.awbNumber}</span>
          {source ? <span className="t13 pc-source">{source}</span> : null}
        </div>
        {check?.failed ? <div className="t13 pc-check"><span aria-hidden="true">? </span>{check.text}</div> : null}
        {late ? <div className="pc-late"><span aria-hidden="true">! </span>{late}</div> : null}
        {zoho ? (
          <div className={`t13 pc-zoho is-${zoho.tone}`}>
            {zoho.text}
            {zoho.retry && p.zohoSyncError ? <span className="pc-zoho__why">: {p.zohoSyncError}</span> : null}
            {zoho.retry && editing ? <>
              {" · "}
              <button type="button" className="sp-link pc-inline" disabled={parcels.busy}
                aria-label={`Retry sending AWB ${p.awbNumber} to Zoho`} onClick={() => void parcels.retryZoho(p)}>Retry</button>
            </> : null}
          </div>
        ) : null}
        {checked[p.id] ? (
          <div className={`t13 pc-note${checked[p.id].ok ? "" : " pc-note--bad"}`} role="status">{checked[p.id].message}</div>
        ) : null}
        {dupes.length ? <div className="t13 pc-note">Same AWB is also on memo {dupes.join(", ")}.</div> : null}
        {photo ? (
          <div className="t13 pc-note">
            {photo.state === "uploading" ? "Uploading slip photo…" : <>
              Slip photo not saved. {photo.message}{" "}
              <button type="button" className="sp-link pc-inline" onClick={() => void parcels.uploadSlip(p.id, photo.file)}>Retry</button>
            </>}
          </div>
        ) : null}
        <div className="pc-actions">
          {editing && deliveredButtonDue(p) ? (
            <button type="button" className="pc-act is-pri" aria-label={`Mark AWB ${p.awbNumber} delivered`}
              onClick={() => setSheet({ kind: "delivered", parcel: p })}>Mark delivered</button>
          ) : null}
          {p.trackingUrl ? (
            <a className="pc-act" href={p.trackingUrl} target="_blank" rel="noopener noreferrer">Open courier page</a>
          ) : null}
          {editing ? shareControl([p], shareLabel) : null}
          {wide || actions.length <= 1
            ? actions.map((a) => (
              <button key={a.key} type="button" className={`pc-act${a.danger ? " is-danger" : ""}`} onClick={a.run}>{a.label}</button>
            ))
            : (
              <button type="button" className="pc-act pc-more" aria-label={`More for AWB ${p.awbNumber}`}
                onClick={() => setSheet({ kind: "menu", parcel: p })}>⋯</button>
            )}
        </div>
      </li>
    );
  };

  return (
    <section className="pc" aria-labelledby={headingId}>
      <div className="sp-row">
        <h3 id={headingId} className="t16 grow pc-title">Parcels{list.length > 1 ? ` (${list.length})` : ""}</h3>
        {editing && list.length > 1 ? shareControl(list, "Share all") : null}
      </div>
      <p className="pc-sr" aria-live="polite">{copied || parcels.announce}</p>
      {status === "error" ? (
        <div className="sp-warn" role="status">
          Couldn't load parcels. <button type="button" className="sp-link pc-inline" onClick={parcels.reload}>Try again</button>
        </div>
      ) : list.length === 0 ? (
        <div className="pc-empty">
          <p className="t13">No parcel yet. Add the AWB when you book the courier.</p>
          <button type="button" className="pc-add is-pri" onClick={() => setSheet({ kind: "add" })}>+ Add parcel</button>
        </div>
      ) : (
        <>
          <ul className="pc-list">{list.map(row)}</ul>
          {editing ? (
            <button type="button" className="pc-add" onClick={() => setSheet({ kind: "add" })}>+ Add parcel</button>
          ) : null}
        </>
      )}

      {sheet?.kind === "add" ? <ParcelFormSheet parcels={parcels} onClose={close} /> : null}
      {sheet?.kind === "edit" ? <ParcelFormSheet parcels={parcels} parcel={sheet.parcel} onClose={close} /> : null}
      {sheet?.kind === "delivered" ? <DeliveredSheet parcels={parcels} parcel={sheet.parcel} onClose={close} /> : null}
      {sheet?.kind === "remove" ? <RemoveSheet parcels={parcels} parcel={sheet.parcel} onClose={close} /> : null}
      {sheet?.kind === "slip" ? <SlipSheet parcel={sheet.parcel} onClose={close} /> : null}
      {sheet?.kind === "copy" ? (
        <SalesSheet label="Message for the customer" onClose={close}>
          <h2>Message for the customer</h2>
          <textarea className="pc-field pc-copy" readOnly value={sheet.text} rows={6} onFocus={(e) => e.currentTarget.select()} />
          <p className="t13">Select the text and copy it into WhatsApp.</p>
          <button type="button" className="sp-btn" onClick={close}>Done</button>
        </SalesSheet>
      ) : null}
      {sheet?.kind === "menu" ? (
        <SalesSheet label={`Parcel ${sheet.parcel.awbNumber}`} onClose={close}>
          <h2>{sheet.parcel.courierName} · <span className="mono">{sheet.parcel.awbNumber}</span></h2>
          <div className="sp-menu">
            {rowActions(sheet.parcel).map((a) => (
              <button key={a.key} type="button" className={a.danger ? "is-danger" : ""} onClick={a.run}>{a.label}</button>
            ))}
          </div>
        </SalesSheet>
      ) : null}
    </section>
  );
};

/** "track.example.in" from a tracking link template; the template itself when it is not a URL. */
export const linkHost = (template: string): string => {
  try {
    return new URL(template.replace("{awb}", "AWB")).hostname.replace(/^www\./, "");
  } catch {
    return template;
  }
};

/** Courier picker: saved couriers, last used first; a new name offers "Add <name>". Escape closes the list before the sheet. */
const CourierCombo = ({ couriers, value, onChange, error }: {
  couriers: DispatchCourier[] | null;
  value: { name: string; id: string; isNew: boolean };
  onChange: (next: { name: string; id: string; isNew: boolean }) => void;
  error?: string;
}) => {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const typed = value.name.trim();
  const matches = useMemo(() => (couriers || []).filter((c) => !c.archived
    && c.name.toLowerCase().includes(typed.toLowerCase())).slice(0, 8), [couriers, typed]);
  const exact = (couriers || []).find((c) => !c.archived && c.name.trim().toLowerCase() === typed.toLowerCase());
  const options: Array<{ key: string; label: string; pick: () => void }> = [
    ...matches.map((c) => ({ key: c.id, label: c.name, pick: () => onChange({ name: c.name, id: c.id, isNew: false }) })),
    ...(typed && !exact ? [{ key: "new", label: `Add “${typed}”`, pick: () => onChange({ name: typed, id: "", isNew: true }) }] : []),
  ];
  const choose = (i: number) => {
    options[i]?.pick();
    setOpen(false);
  };
  return (
    <div className="pc-combo">
      <label className="pc-label" htmlFor={`${listId}-input`}>Courier</label>
      <input id={`${listId}-input`} className="pc-field" role="combobox" aria-expanded={open && options.length > 0}
        aria-controls={listId} aria-autocomplete="list" autoComplete="off" autoFocus
        aria-activedescendant={open && options[active] ? `${listId}-${active}` : undefined}
        aria-invalid={!!error} aria-describedby={error ? `${listId}-err` : undefined}
        placeholder={couriers === null ? "Loading couriers…" : "e.g. Tirupati Courier"} value={value.name}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onChange={(e) => {
          const name = e.target.value;
          const same = (couriers || []).find((c) => !c.archived && c.name.trim().toLowerCase() === name.trim().toLowerCase());
          onChange(same ? { name, id: same.id, isNew: false } : { name, id: "", isNew: false });
          setOpen(true);
          setActive(0);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((i) => Math.min(options.length - 1, i + 1)); }
          if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
          if (e.key === "Enter" && open && options.length) { e.preventDefault(); choose(active); }
          if (e.key === "Escape" && open && options.length) {
            // The list closes first; the next Escape closes the sheet.
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            setOpen(false);
          }
        }} />
      {open && options.length ? (
        <ul id={listId} role="listbox" className="pc-options" aria-label="Couriers">
          {options.map((o, i) => (
            <li key={o.key} id={`${listId}-${i}`} role="option" aria-selected={i === active}
              className={`${i === active ? "is-on" : ""}${o.key === "new" ? " is-new" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); choose(i); }}>{o.label}</li>
          ))}
        </ul>
      ) : null}
      {error ? <span id={`${listId}-err`} className="pc-err">{error}</span> : null}
    </div>
  );
};

/** Add a parcel, or change one that is still Booked. Saves first; the slip photo uploads after. */
const ParcelFormSheet = ({ parcels, parcel, onClose }: { parcels: MemoParcels; parcel?: ParcelView; onClose: () => void }) => {
  const ids = useId();
  const today = todayIso();
  const [couriers, setCouriers] = useState<DispatchCourier[] | null>(null);
  const [courier, setCourier] = useState({ name: parcel?.courierName || "", id: parcel?.courierId || "", isNew: false });
  const [link, setLink] = useState("");
  /** Editing the saved courier's tracking link: null while closed. */
  const [savedLink, setSavedLink] = useState<string | null>(null);
  const [awb, setAwb] = useState(parcel?.awbNumber || "");
  const [bookedOn, setBookedOn] = useState(parcel?.bookedOn || today);
  const [dateOpen, setDateOpen] = useState(false);
  const [slip, setSlip] = useState<File | null>(null);
  const [errors, setErrors] = useState<{ courier?: string; awb?: string; link?: string; form?: string }>({});

  useEffect(() => {
    let live = true;
    Promise.resolve(listDispatchCouriers())
      .then((res) => live && setCouriers(res?.success && Array.isArray(res.data) ? res.data : []))
      .catch(() => live && setCouriers([]));
    return () => { live = false; };
  }, []);

  const saved = courier.id ? (couriers || []).find((c) => c.id === courier.id) || null : null;

  const save = async () => {
    const name = courier.name.trim();
    const template = courier.isNew || (!courier.id && name) ? templateFromPastedUrl(link, awb) : "";
    const newSaved = saved && savedLink !== null ? templateFromPastedUrl(savedLink, awb) : null;
    const next = {
      courier: name ? undefined : "Choose or type a courier",
      awb: isValidAwb(awb) ? undefined : "An AWB number is 4 to 30 letters and digits",
      link: template ? templateProblem(template) || undefined : newSaved ? templateProblem(newSaved) || undefined : undefined,
    };
    setErrors(next);
    if (next.courier || next.awb || next.link) return;
    // A changed link on a saved courier is saved first, so this parcel already gets it.
    if (saved && newSaved !== null && newSaved !== (saved.trackingUrlTemplate || "")) {
      try {
        const res = await updateDispatchCourier(saved.id, { trackingUrlTemplate: newSaved });
        if (!res?.success || !res.data) {
          setErrors({ link: msg(res, "Tracking link not saved") });
          return;
        }
        const updated = res.data;
        setCouriers((cur) => (cur || []).map((c) => (c.id === updated.id ? updated : c)));
      } catch {
        setErrors({ form: offline });
        return;
      }
    }
    const who: ParcelCourierInput = courier.id ? { courierId: courier.id } : { courierName: name, ...(template ? { trackingUrlTemplate: template } : {}) };
    const done = parcel
      // The parcel's own courier is left out, so an edit still saves after that courier was archived.
      ? await parcels.edit(parcel, { ...(courier.id && courier.id === parcel.courierId ? {} : who), awbNumber: awb.trim(), bookedOn })
      : await parcels.add({ ...who, awbNumber: awb.trim(), bookedOn }, slip);
    if (done.ok) onClose();
    else setErrors({ form: done.message });
  };

  return (
    <SalesSheet label={parcel ? "Edit parcel" : "Add parcel"} onClose={onClose}>
      <form className="pc-form" noValidate onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <h2>{parcel ? "Edit parcel" : "Add parcel"}</h2>
        <div className="pc-grid">
          <CourierCombo couriers={couriers} value={courier} error={errors.courier}
            onChange={(v) => { setCourier(v); setSavedLink(null); setErrors((cur) => ({ ...cur, courier: undefined, link: undefined })); }} />
          <div>
            <label className="pc-label" htmlFor={`${ids}-awb`}>AWB number</label>
            <input id={`${ids}-awb`} className="pc-field mono" value={awb} autoCapitalize="characters" spellCheck={false}
              autoComplete="off" autoCorrect="off" inputMode="text" maxLength={40} aria-invalid={!!errors.awb}
              aria-describedby={errors.awb ? `${ids}-awb-err` : undefined}
              onChange={(e) => { setAwb(e.target.value); setErrors((cur) => ({ ...cur, awb: undefined })); }} />
            {errors.awb ? <span id={`${ids}-awb-err`} className="pc-err">{errors.awb}</span> : null}
          </div>
        </div>
        {courier.isNew || (!courier.id && courier.name.trim() && couriers !== null) ? (
          <div>
            <label className="pc-label" htmlFor={`${ids}-link`}>Tracking link (optional)</label>
            <input id={`${ids}-link`} className="pc-field" type="url" inputMode="url" autoComplete="off" spellCheck={false}
              placeholder="https://" value={link} aria-invalid={!!errors.link} aria-describedby={`${ids}-link-hint`}
              onChange={(e) => { setLink(e.target.value); setErrors((cur) => ({ ...cur, link: undefined })); }} />
            <span id={`${ids}-link-hint`} className={errors.link ? "pc-err" : "t13"}>
              {errors.link || "Open this AWB on the courier's site and paste the page link. Next time the link fills itself."}
            </span>
          </div>
        ) : null}
        {saved ? (
          savedLink === null ? (
            <div className="sp-row pc-saved-link">
              <span className="t13 grow clip">
                {saved.trackingUrlTemplate ? <>Tracking link: {linkHost(saved.trackingUrlTemplate)}</> : "No tracking link for this courier"}
              </span>
              <button type="button" className="sp-link pc-inline" onClick={() => setSavedLink(saved.trackingUrlTemplate || "")}>
                {saved.trackingUrlTemplate ? "Edit link" : "Add link"}
              </button>
            </div>
          ) : (
            <div>
              <label className="pc-label" htmlFor={`${ids}-saved-link`}>Tracking link for {saved.name}</label>
              <input id={`${ids}-saved-link`} className="pc-field" type="url" inputMode="url" autoComplete="off" spellCheck={false}
                placeholder="https://" value={savedLink} aria-invalid={!!errors.link} aria-describedby={`${ids}-saved-link-hint`}
                onChange={(e) => { setSavedLink(e.target.value); setErrors((cur) => ({ ...cur, link: undefined })); }} />
              <span id={`${ids}-saved-link-hint`} className={errors.link ? "pc-err" : "t13"}>
                {errors.link || "Paste this AWB's page on the courier's site, or leave empty to remove. Used for parcels added from now on."}
              </span>
            </div>
          )
        ) : null}
        {!parcel ? (
          <label className={`pc-slip${slip ? " is-on" : ""}`}>
            <input type="file" accept="image/*" capture="environment" className="pc-sr"
              onChange={(e) => setSlip(e.target.files?.[0] || null)} />
            <span aria-hidden="true" className="pc-slip__icon">⌗</span>
            <span className="grow">{slip ? <>Slip photo: <b className="clip">{slip.name || "photo"}</b></> : "Add slip photo (only your studio sees it)"}</span>
            {slip ? <button type="button" className="sp-link pc-inline" onClick={(e) => { e.preventDefault(); setSlip(null); }}>Remove</button> : null}
          </label>
        ) : null}
        <div className="sp-row pc-date">
          {dateOpen ? (
            <label className="grow">
              <span className="pc-label">Shipped on</span>
              <input className="pc-field" type="date" value={bookedOn} max={today} onChange={(e) => setBookedOn(e.target.value || today)} />
            </label>
          ) : (
            <>
              <span className="t13 grow">Shipped {bookedOn === today ? "today" : `on ${dayLabel(bookedOn)}`}</span>
              <button type="button" className="sp-link pc-inline" onClick={() => setDateOpen(true)}>Change date</button>
            </>
          )}
        </div>
        {errors.form ? <div className="sp-warn" role="alert">{errors.form}</div> : null}
        <div className="pc-foot sp-btns">
          <button type="button" className="sp-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="sp-btn is-pri" disabled={parcels.busy}>{parcels.busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </SalesSheet>
  );
};

/** Mark delivered by hand: date (default today, not before shipping, not after today) and an optional note. */
const DeliveredSheet = ({ parcels, parcel, onClose }: { parcels: MemoParcels; parcel: ParcelView; onClose: () => void }) => {
  const ids = useId();
  const today = todayIso();
  const [date, setDate] = useState(today < parcel.bookedOn ? parcel.bookedOn : today);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const save = async () => {
    if (!date || date > today || date < parcel.bookedOn) {
      setError(`Pick a date from ${dayLabel(parcel.bookedOn)} to today`);
      return;
    }
    const done = await parcels.markDelivered(parcel, date, note);
    if (done.ok) onClose();
    else setError(done.message);
  };
  return (
    <SalesSheet label="Mark delivered" onClose={onClose}>
      <form className="pc-form" noValidate onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <h2>Mark delivered?</h2>
        <p className="t13">{parcel.courierName} · <span className="mono">{parcel.awbNumber}</span>. Your name and the time are kept with it.</p>
        <label>
          <span className="pc-label">Delivered on</span>
          <input id={`${ids}-date`} className="pc-field" type="date" value={date} min={parcel.bookedOn} max={today} autoFocus
            onChange={(e) => setDate(e.target.value)} />
        </label>
        <label>
          <span className="pc-label">Note (optional)</span>
          <input className="pc-field" value={note} maxLength={200} placeholder="e.g. Customer confirmed on call"
            onChange={(e) => setNote(e.target.value)} />
        </label>
        {error ? <div className="sp-warn" role="alert">{error}</div> : null}
        <div className="pc-foot sp-btns">
          <button type="button" className="sp-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="sp-btn is-pri" disabled={parcels.busy}>{parcels.busy ? "Saving…" : "Mark delivered"}</button>
        </div>
      </form>
    </SalesSheet>
  );
};

/** Remove a parcel added by mistake: a reason is required and kept in its history. */
const RemoveSheet = ({ parcels, parcel, onClose }: { parcels: MemoParcels; parcel: ParcelView; onClose: () => void }) => {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const save = async () => {
    if (!reason.trim()) {
      setError("Say why the parcel is removed");
      return;
    }
    const done = await parcels.remove(parcel, reason);
    if (done.ok) onClose();
    else setError(done.message);
  };
  return (
    <SalesSheet label="Remove parcel" onClose={onClose}>
      <form className="pc-form" noValidate onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <h2>Remove this parcel?</h2>
        <p className="t13">{parcel.courierName} · <span className="mono">{parcel.awbNumber}</span>. It leaves this memo; its history stays.</p>
        <label>
          <span className="pc-label">Reason</span>
          <input className="pc-field" value={reason} maxLength={200} autoFocus placeholder="e.g. Wrong AWB typed"
            onChange={(e) => { setReason(e.target.value); setError(""); }} />
        </label>
        {error ? <div className="sp-warn" role="alert">{error}</div> : null}
        <div className="pc-foot sp-btns">
          <button type="button" className="sp-btn" onClick={onClose}>Keep parcel</button>
          <button type="submit" className="sp-btn is-danger" disabled={parcels.busy}>{parcels.busy ? "Removing…" : "Remove parcel"}</button>
        </div>
      </form>
    </SalesSheet>
  );
};

/** The slip photo (a signed link valid for minutes, fetched only now) and the parcel's history. */
const SlipSheet = ({ parcel, onClose }: { parcel: ParcelView; onClose: () => void }) => {
  const [detail, setDetail] = useState<ParcelDetail | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    getParcel(parcel.id)
      .then((res) => {
        if (!live) return;
        if (res?.success && res.data) setDetail(res.data);
        else setError(msg(res, "Couldn't open the slip photo"));
      })
      .catch(() => live && setError(offline));
    return () => { live = false; };
  }, [parcel.id]);
  return (
    <SalesSheet label="Courier slip" onClose={onClose}>
      <h2>Courier slip</h2>
      <p className="t13">{parcel.courierName} · <span className="mono">{parcel.awbNumber}</span> · only your studio sees this photo.</p>
      {error ? <div className="sp-warn" role="alert">{error}</div> : null}
      {!detail && !error ? <p className="t13" role="status">Loading…</p> : null}
      {detail?.slipPhotoUrl ? (
        <img className="pc-slip-photo" src={detail.slipPhotoUrl} alt={`Courier slip for AWB ${parcel.awbNumber}`} />
      ) : detail ? <p className="t13">No slip photo on this parcel.</p> : null}
      {detail?.events?.length ? (
        <ol className="pc-history" aria-label="History">
          {detail.events.map((e, i) => (
            <li key={i} className="t13">
              {new Date(e.at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} · {e.actorName === "system" ? "Courier check" : e.actorName || "Someone"}:{" "}
              {e.kind === "ADDED" ? "added" : e.kind === "REMOVED" ? "removed" : e.kind === "ZOHO_PUSHED" ? "AWB written to Zoho notes"
                : e.kind === "CHECK_FAILED" ? "couldn't check the courier"
                : e.kind === "STAGE_AUTO" ? `courier says ${STAGES[e.toStage || "BOOKED"]?.label.toLowerCase() || "stage"}`
                : e.kind === "STAGE_MANUAL"
                ? `marked ${STAGES[e.toStage || "BOOKED"]?.label.toLowerCase() || "stage"}` : (e.statusText || "changed").toLowerCase()}
              {e.reason ? ` (${e.reason})` : ""}
            </li>
          ))}
        </ol>
      ) : null}
      <button type="button" className="sp-btn" onClick={onClose}>Close</button>
    </SalesSheet>
  );
};

/**
 * Sales hub: an amber chip with the studio's parcels not delivered the set number of days (15) after booking, only when there are
 * any. Tap lists them; each opens its memo.
 */
export const OverdueParcelsChip = ({ role }: { role?: string }) => {
  const history = useHistory();
  const allowed = canUseParcels(role);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState<ParcelView[]>([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (offset: number) => {
    setLoading(true);
    setError("");
    try {
      const res = await listOverdueParcels(offset, 20);
      if (!res?.success || !res.data) {
        if (!isParcelsOff(res)) setError(msg(res, "Couldn't load parcels"));
        if (offset === 0) setTotal(0);
        return;
      }
      const page = res.data;
      setTotal(page.total);
      setItems((cur) => (offset === 0 ? page.items : [...cur, ...page.items]));
    } catch {
      setError(offline);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) void load(0);
  }, [allowed, load]);

  if (!allowed || total <= 0) return null;
  const days = items[0]?.overdueDays ?? OVERDUE_DAYS;
  return (
    <>
      <button type="button" className="pc-late pc-late--chip" onClick={() => setOpen(true)}>
        <span aria-hidden="true">! </span>{total} {total === 1 ? "parcel" : "parcels"} not delivered after {days} days
      </button>
      {open ? (
        <SalesSheet label="Parcels not delivered" onClose={() => setOpen(false)}>
          <h2>Not delivered after {days} days</h2>
          <p className="t13">Check with the courier, then mark each one delivered on its memo.</p>
          {error ? <div className="sp-warn" role="alert">{error}</div> : null}
          <ul className="pc-list">
            {items.map((p) => (
              <li key={p.id}>
                <button type="button" className="pc-overdue" onClick={() => history.push(`/sales/memos/${p.memoId}`)}>
                  <span className="t16">{p.courierName} · <span className="mono">{p.awbNumber}</span></span>
                  <span className="t13">Memo <span className="mono">{p.memoCode}</span> · shipped {dayLabel(p.bookedOn)}</span>
                </button>
              </li>
            ))}
          </ul>
          {loading ? <p className="t13" role="status">Loading…</p> : null}
          {!loading && items.length < total ? (
            <button type="button" className="sp-btn" onClick={() => void load(items.length)}>Show more</button>
          ) : null}
        </SalesSheet>
      ) : null}
    </>
  );
};
